/**
 * The planet in chunks: tiles grouped around directions spread evenly over the sphere, one mesh
 * each, so the camera skips what it can't see. three.js skips chunks out of view from each mesh's
 * bounding sphere; `overHorizon` tells which lie on the far side of the planet.
 */
import type { Grid } from '@bramvasterd/terrella-rules';

import { SEG, type Layout } from './layout';

export interface ChunkLayout {
  count: number;
  /** Per tile: its chunk, and its first vertex inside that chunk. */
  chunkOf: Int32Array;
  vertexOf: Int32Array;
  /** Per chunk: how many vertices it has, and its triangles in its own numbering. */
  vertices: Int32Array;
  index: Uint32Array[];
  /** Per chunk: the unit direction it centres on (3 floats each). */
  dir: Float32Array;
  /** Per chunk: how far from that direction its tiles reach, in radians. */
  reach: Float32Array;
}

/** About this many tiles in a chunk: few draw calls, yet small enough to skip. */
const TILES_PER_CHUNK = 40;
const MAX_CHUNKS = 64;

/** `n` directions spread evenly over the sphere, on a Fibonacci spiral. */
function spread(n: number): Float32Array {
  const out = new Float32Array(n * 3);
  const golden = Math.PI * (3 - Math.sqrt(5));
  for (let k = 0; k < n; k++) {
    const y = n === 1 ? 1 : 1 - (2 * k) / (n - 1);
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    out[k * 3] = Math.cos(golden * k) * r;
    out[k * 3 + 1] = y;
    out[k * 3 + 2] = Math.sin(golden * k) * r;
  }
  return out;
}

const dot = (a: ArrayLike<number>, i: number, b: ArrayLike<number>, j: number): number =>
  (a[i] ?? 0) * (b[j] ?? 0) + (a[i + 1] ?? 0) * (b[j + 1] ?? 0) + (a[i + 2] ?? 0) * (b[j + 2] ?? 0);

const layouts = new Map<number, ChunkLayout>();

/** The chunks of a grid size, built once and kept. */
export function chunksFor(grid: Grid, layout: Layout): ChunkLayout {
  const cached = layouts.get(grid.subdivisions);
  if (cached) return cached;
  const count = Math.max(1, Math.min(MAX_CHUNKS, Math.round(grid.count / TILES_PER_CHUNK)));
  const dir = spread(count);
  const chunkOf = new Int32Array(grid.count);
  const vertexOf = new Int32Array(grid.count);
  const vertices = new Int32Array(count);
  for (let i = 0; i < grid.count; i++) {
    let best = 0;
    let bestDot = -2;
    for (let k = 0; k < count; k++) {
      const d = dot(grid.centers, i * 3, dir, k * 3);
      if (d > bestDot) {
        bestDot = d;
        best = k;
      }
    }
    chunkOf[i] = best;
    vertexOf[i] = vertices[best] ?? 0;
    vertices[best] = (vertices[best] ?? 0) + (layout.off[i + 1] ?? 0) - (layout.off[i] ?? 0);
  }
  // each tile's triangles, from the planet's layout, renumbered for its chunk
  const lists: number[][] = Array.from({ length: count }, () => []);
  const reach = new Float32Array(count);
  let at = 0;
  for (let i = 0; i < grid.count; i++) {
    const corners = grid.corners[i] ?? new Float64Array();
    const nc = corners.length / 3;
    const n = 3 * (nc + 2 * nc * (SEG + 1));
    const c = chunkOf[i] ?? 0;
    const shift = (vertexOf[i] ?? 0) - (layout.off[i] ?? 0);
    const list = lists[c] ?? [];
    for (let k = 0; k < n; k++) list.push((layout.index[at + k] ?? 0) + shift);
    at += n;
    for (let k = 0; k < nc; k++) {
      const angle = Math.acos(Math.min(1, Math.max(-1, dot(corners, k * 3, dir, c * 3))));
      if (angle > (reach[c] ?? 0)) reach[c] = angle;
    }
  }
  const chunks: ChunkLayout = {
    count,
    chunkOf,
    vertexOf,
    vertices,
    index: lists.map((list) => new Uint32Array(list)),
    dir,
    reach,
  };
  layouts.set(grid.subdivisions, chunks);
  return chunks;
}

/**
 * Whether chunk `k` lies wholly beyond the horizon for a camera at (`x`, `y`, `z`), in the planet's
 * own space, over a planet of radius `radius`. Generous: tall tiles and walls peek over the edge.
 */
export function overHorizon(
  chunks: ChunkLayout,
  k: number,
  x: number,
  y: number,
  z: number,
  radius: number,
): boolean {
  const d = Math.hypot(x, y, z);
  const ground = radius - 0.2;
  const top = radius + 0.4;
  if (d <= top) return false;
  const facing =
    ((chunks.dir[k * 3] ?? 0) * x +
      (chunks.dir[k * 3 + 1] ?? 0) * y +
      (chunks.dir[k * 3 + 2] ?? 0) * z) /
    d;
  const angle = Math.acos(Math.min(1, Math.max(-1, facing)));
  const horizon = Math.acos(ground / d) + Math.acos(ground / top);
  return angle - (chunks.reach[k] ?? 0) > horizon;
}
